import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FeatureFlagsResponse, getFeatureFlags, updateFeatureFlags } from "../../api/feature-flags";
import {
  buildFinalPayload,
  CHANGE_TYPE_ADD,
  CHANGE_TYPE_REMOVE,
  stageChangeHelper,
  StagedChange,
} from "../../domain/feature-flags";
import { useAppContext } from "../contexts/app";
import useMetriportToast from "../shared/toast";
import { getErrorMessageInternal } from "../../shared/error";
import { captureAndDisplayError } from "../shared/util";
import { ChangesetPanel } from "./ChangesetPanel";
import { ConfirmApplyDialog } from "./ConfirmApplyDialog";
import { FeatureFlagSection } from "./FeatureFlagSection";

export default function FeatureFlagManagementPage() {
  const { state } = useAppContext();
  const toast = useMetriportToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [featureFlagsData, setFeatureFlagsData] = useState<FeatureFlagsResponse | undefined>();
  const [searchQuery, setSearchQuery] = useState("");
  const [changes, setChanges] = useState<StagedChange[]>([]);

  const {
    isOpen: isConfirmDialogOpen,
    onOpen: onConfirmDialogOpen,
    onClose: onConfirmDialogClose,
  } = useDisclosure();

  useEffect(() => {
    if (!state.isLoaded) return;

    async function fetchData() {
      try {
        setIsLoading(true);
        const data = await getFeatureFlags();
        setFeatureFlagsData(data);
      } catch (error) {
        captureAndDisplayError({
          error,
          msg: "Failed to load feature flags",
          context: "fetchFeatureFlags",
          captureMsg: "FeatureFlagManagementPage - fetch failed",
          toast,
        });
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [state.isLoaded]);

  function handleToggleChange(change: StagedChange) {
    setChanges(prev => stageChangeHelper(prev, change));
  }

  function handleStageRemoval(flagName: string, cxId: string) {
    // Check if this is a pending addition - if so, remove it from staging
    const isPendingAddition = changes.some(
      c => c.flagName === flagName && c.cxId === cxId && c.type === CHANGE_TYPE_ADD
    );

    if (isPendingAddition) {
      // Remove the pending addition directly
      setChanges(prev =>
        prev.filter(
          c => !(c.flagName === flagName && c.cxId === cxId && c.type === CHANGE_TYPE_ADD)
        )
      );
    } else {
      // Check if already pending removal - if so, unstage it
      const isPendingRemoval = changes.some(
        c => c.flagName === flagName && c.cxId === cxId && c.type === CHANGE_TYPE_REMOVE
      );

      if (isPendingRemoval) {
        // Remove the pending removal (unstage)
        setChanges(prev =>
          prev.filter(
            c => !(c.flagName === flagName && c.cxId === cxId && c.type === CHANGE_TYPE_REMOVE)
          )
        );
      } else {
        // Stage a new removal
        handleToggleChange({ type: CHANGE_TYPE_REMOVE, flagName, cxId });
      }
    }
  }

  function handleApplyChanges() {
    onConfirmDialogOpen();
  }

  async function applyChanges() {
    if (!featureFlagsData) return;

    try {
      setIsApplying(true);

      const latestData = await getFeatureFlags();
      const freshData =
        latestData.version !== featureFlagsData.version ? latestData : featureFlagsData;

      if (latestData.version !== featureFlagsData.version) {
        setFeatureFlagsData(latestData);
      }

      const finalPayload = buildFinalPayload(freshData.featureFlags, changes);
      if (!state.user?.attributes?.email) {
        throw new Error("No user email found in Cognito user attributes.");
      }
      const updated = await updateFeatureFlags({
        featureFlags: finalPayload,
        existingVersion: freshData.version,
        updatedBy: state.user.attributes.email,
      });

      setFeatureFlagsData(updated);
      setChanges([]);
      onConfirmDialogClose();
      toast.success({ title: "Feature flags updated successfully!" });
    } catch (err) {
      const message = getErrorMessageInternal(err);
      toast.error({ title: "Error", description: message });
    } finally {
      setIsApplying(false);
    }
  }

  function matchesSearch(cxId: string): boolean {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const customer = state.customerMap?.[cxId];
    const customerName = customer?.rootCustomer.email?.toLowerCase() ?? "";
    return cxId.toLowerCase().includes(query) || customerName.includes(query);
  }

  const flagEntries = featureFlagsData
    ? Object.entries(featureFlagsData.featureFlags)
        .filter(([, flag]) => flag.values !== undefined) // Exclude global flags
        .filter(([flagName, flag]) => {
          if (!searchQuery.trim()) return true;
          const values = flag.values ?? [];
          const pendingAdditions = changes.filter(
            c => c.flagName === flagName && c.type === CHANGE_TYPE_ADD
          );
          return values.some(matchesSearch) || pendingAdditions.some(c => matchesSearch(c.cxId));
        })
        .sort(([a], [b]) => a.localeCompare(b))
    : [];

  return (
    <Box>
      <VStack p={10} align="start" gap={4}>
        <Flex w="full" alignItems="center" justifyContent="space-between">
          <Text fontSize="2xl" fontWeight="bold">
            Feature Flag Management
          </Text>
        </Flex>

        {isLoading ? (
          <Skeleton h={200} w="full" />
        ) : !featureFlagsData ? (
          <Text color="red.500">Failed to load feature flags</Text>
        ) : (
          <Box w="full">
            <ChangesetPanel
              changes={changes}
              customerMap={state.customerMap ?? {}}
              featureFlags={featureFlagsData.featureFlags}
              onToggleChange={handleToggleChange}
              onApply={handleApplyChanges}
              onClear={() => setChanges([])}
              isApplying={isApplying}
            />

            <Text fontSize="xl" fontWeight="semibold" mb={4}>
              Current Feature Flag State
            </Text>

            <InputGroup mb={4} maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search via customer name or cx id here..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                bg="white"
              />
            </InputGroup>

            <Text fontSize="sm" color="gray.500" mb={4}>
              Last updated by {featureFlagsData.updatedBy} at{" "}
              {new Date(featureFlagsData.updatedAt).toLocaleString()} (version{" "}
              {featureFlagsData.version})
            </Text>

            {flagEntries.map(([flagName, flag]) => (
              <FeatureFlagSection
                key={flagName}
                flagName={flagName}
                flag={flag}
                customerMap={state.customerMap ?? {}}
                searchQuery={searchQuery}
                changes={changes}
                onStageRemoval={cxId => handleStageRemoval(flagName, cxId)}
              />
            ))}
          </Box>
        )}
      </VStack>

      <ConfirmApplyDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onConfirm={applyChanges}
        changes={changes}
        customerMap={state.customerMap ?? {}}
        isApplying={isApplying}
      />
    </Box>
  );
}
